import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import tradeDataStore from "@/store/tradeDataStore";
import { IOrderFill } from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import { isFillCancelled } from "../../fillStateHelpers";
import { NotationToDecimalValueType } from "@/lib/interfaces and Enums/TYPENotations";
import {
  returnDecimalFormatting,
  isPriceNotation,
} from "@/lib/globalFormatterHelper";
import {
  containsHyphenInQuote,
  convertToDollarFraction,
  dollarFractionToDecimal,
  formatDollarFractionString,
} from "@/lib/businessRules/dollarFractionBusinessRule";

/**
 * Fill value types for calculating sent vs pending totals.
 * - "sent": Values that have been sent to the OP (what OP currently sees)
 * - "pending": Values that will be sent after update (what OP will see)
 */
export type FillValueType = "sent" | "pending";

/**
 * Gets the appropriate fillRate and fillSize for a fill based on the value type.
 *
 * For "sent" (what OP currently sees):
 * - undefined/Unchanged: use fillRate, fillSize (no changes made)
 * - Updated: use historicFillRate, historicFillSize (original sent values)
 * - Cancel: use historicFillRate, historicFillSize (original sent values)
 * - New: exclude (not sent yet)
 *
 * For "pending" (what OP will see after update):
 * - undefined/Unchanged: use fillRate, fillSize
 * - Updated: use fillRate, fillSize (new values)
 * - Cancel: exclude (will be cancelled)
 * - New: use fillRate, fillSize (will be added)
 *
 * @param fill - The fill object
 * @param valueType - "sent" or "pending"
 * @returns { rate, size, include } - The values to use and whether to include in calculation
 */
/**
 * Safely parses a string rate to a number.
 * Returns undefined if the value is null, undefined, or not a valid number.
 */
function parseRate(rate: string | undefined): number | undefined {
  if (rate === undefined || rate === null || rate === "") return undefined;
  // $fr: convert fraction string to decimal for calculations
  if (typeof rate === "string" && containsHyphenInQuote(rate)) {
    return dollarFractionToDecimal(rate);
  }
  const parsed = parseFloat(rate);
  return isNaN(parsed) ? undefined : parsed;
}

export function getFillValuesForCalculation(
  fill: IOrderFill,
  valueType: FillValueType,
): { rate: number | undefined; size: number | undefined; include: boolean } {
  const fillType = fill.fillType;

  if (valueType === "sent") {
    // For "sent" values - what OP currently sees
    switch (fillType) {
      case "New":
        // New fills haven't been sent yet - exclude
        return { rate: undefined, size: undefined, include: false };
      case "Updated":
      case "Cancel":
        // Use historic values (what was originally sent)
        return {
          rate: parseRate(fill.historicFillRate),
          size: fill.historicFillSize,
          include: true,
        };
      case "Unchanged":
      case undefined:
      default:
        // No changes - use current values
        return {
          rate: parseRate(fill.fillRate),
          size: fill.fillSize,
          include: true,
        };
    }
  } else {
    // For "pending" values - what OP will see after update
    switch (fillType) {
      case "Cancel":
        // Cancelled fills will be removed - exclude
        return { rate: undefined, size: undefined, include: false };
      case "New":
      case "Updated":
      case "Unchanged":
      case undefined:
      default:
        // Use current values (new or updated values)
        return {
          rate: parseRate(fill.fillRate),
          size: fill.fillSize,
          include: true,
        };
    }
  }
}

/**
 * Calculates the total filled quantity based on value type (sent vs pending).
 *
 * @param fills - Array of fills
 * @param valueType - "sent" or "pending"
 * @returns Total quantity
 */
export function calculateTotalQuantityByType(
  fills: IOrderFill[],
  valueType: FillValueType,
): number {
  if (!fills || fills.length === 0) {
    return 0;
  }

  return fills.reduce((sum, fill) => {
    const { size, include } = getFillValuesForCalculation(fill, valueType);
    if (include && size !== undefined && size !== null) {
      return sum + size;
    }
    return sum;
  }, 0);
}

/**
 * Calculates the blended weighted average price based on value type (sent vs pending).
 * Formula: Σ(rate × size) / Σ(size)
 *
 * @param fills - Array of fills
 * @param valueType - "sent" or "pending"
 * @returns Weighted average price or null if no valid data
 */
export function calculateBlendedPriceByType(
  fills: IOrderFill[],
  valueType: FillValueType,
): number | null {
  if (!fills || fills.length === 0) {
    return null;
  }

  let totalWeightedPrice = 0;
  let totalQuantity = 0;

  for (const fill of fills) {
    const { rate, size, include } = getFillValuesForCalculation(
      fill,
      valueType,
    );
    if (include && rate !== undefined && rate !== null && size) {
      totalWeightedPrice += rate * size;
      totalQuantity += size;
    }
  }

  if (totalQuantity === 0) {
    return null;
  }

  return totalWeightedPrice / totalQuantity;
}

/**
 * Calculates the total filled percentage based on value type (sent vs pending).
 * Formula: (Σ size / blockNotional) × 100
 *
 * @param fills - Array of fills
 * @param blockNotional - The total notional of the block
 * @param valueType - "sent" or "pending"
 * @returns Percentage filled (0-100)
 */
export function calculateFilledPercentByType(
  fills: IOrderFill[],
  blockNotional: number,
  valueType: FillValueType,
): number {
  if (!fills || fills.length === 0 || !blockNotional || blockNotional === 0) {
    return 0;
  }

  const totalFilled = calculateTotalQuantityByType(fills, valueType);
  return (totalFilled / blockNotional) * 100;
}

/**
 * Checks if there are any pending changes (New, Updated, or Cancel fills).
 * Used to determine if tooltips should be shown.
 *
 * @param fills - Array of fills
 * @returns True if there are pending changes
 */
export function hasPendingChanges(fills: IOrderFill[]): boolean {
  if (!fills || fills.length === 0) {
    return false;
  }

  return fills.some(
    (fill) =>
      fill.fillType === "New" ||
      fill.fillType === "Updated" ||
      fill.fillType === "Cancel",
  );
}

/**
 * Formats a price value for display with notation rounding applied.
 * Uses returnDecimalFormatting to apply proper decimal places based on notation.
 * For $fr: returns the fraction string as-is.
 *
 * @param price - The price to format (number, string for $fr, or null)
 * @param notation - The notation type for determining decimal places
 * @returns Formatted price string (value only, no notation suffix)
 */
export function formatPriceForDisplay(
  price: number | string | null,
  notation?: NotationToDecimalValueType,
): string {
  if (price === null) {
    return "";
  }

  // $fr: return fraction string as-is, or convert decimal back to fraction
  if (notation === "$fr") {
    if (typeof price === "string") return price;
    if (typeof price === "number") {
      const fractionResult = convertToDollarFraction(price);
      // If it converted to a valid fraction (contains hyphen), format it
      if (fractionResult.includes("-")) {
        return formatDollarFractionString(fractionResult);
      }
      // Otherwise stay as decimal with $fr decimal places (8dp)
      return returnDecimalFormatting(price, "$fr", true) || "";
    }
  }

  const numPrice = typeof price === "number" ? price : parseFloat(price);
  if (isNaN(numPrice)) return "";

  // Apply notation-based rounding
  return returnDecimalFormatting(numPrice, notation, true) || "";
}

/**
 * Formats a price value for display WITH notation suffix.
 * E.g., 15 with "bps" -> "15 bps", 4.5 with "%" -> "4.5 %"
 * When price is null/empty, returns notation as placeholder.
 * For $fr: displays the fraction string with " $fr" suffix.
 *
 * @param price - The price to format (number, string for $fr, or null)
 * @param notation - The notation type for determining decimal places and suffix
 * @returns Formatted price string with notation suffix, or notation as placeholder
 */
export function formatPriceWithNotation(
  price: number | string | null,
  notation?: NotationToDecimalValueType,
): string {
  if (price === null) {
    // Return "price" placeholder for $p/$fr, otherwise notation
    return isPriceNotation(notation) ? "price" : notation || "";
  }

  // $fr: return fraction string without suffix, or convert decimal back to fraction
  if (notation === "$fr") {
    if (typeof price === "string") return price; // Removed the $fr
    if (typeof price === "number") {
      const fractionResult = convertToDollarFraction(price);
      // If it converted to a valid fraction (contains hyphen), format it
      if (fractionResult.includes("-")) {
        return formatDollarFractionString(fractionResult);
      }
      // Otherwise stay as decimal with $fr decimal places (8dp)
      return returnDecimalFormatting(price, "$fr", true) || "";
    }
  }

  const numPrice = typeof price === "number" ? price : parseFloat(price);
  if (isNaN(numPrice))
    return isPriceNotation(notation) ? "price" : notation || "";

  const formattedValue = returnDecimalFormatting(numPrice, notation, true);
  if (!formattedValue) {
    // Return "price" placeholder for $p/$fr, otherwise notation
    return isPriceNotation(notation) ? "price" : notation || "";
  }

  // Price notations ($p, $fr): show value WITHOUT suffix
  if (isPriceNotation(notation)) {
    return formattedValue;
  }

  const notationSuffix = notation || "";
  return `${formattedValue} ${notationSuffix}`.trim();
}

/**
 * Formats a quantity value for display (with appropriate units).
 *
 * @param quantity - The quantity to format
 * @returns Formatted quantity string
 */
export function formatQuantityForDisplay(quantity: number): string {
  if (quantity >= 1000000) {
    return `${(quantity / 1000000).toFixed(2).replace(/\.?0+$/, "")}M`;
  } else if (quantity >= 1000) {
    return `${(quantity / 1000).toFixed(2).replace(/\.?0+$/, "")}K`;
  }
  return quantity.toString();
}

/**
 * Gets the instrument type from the Zustand store for a given leg.
 * Used for formatting quantities with appropriate notation (e.g., "lots" for FUT).
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID
 * @returns The instrument type string or undefined
 */
export function getInstrumentTypeFromStore(
  esid: string,
  blockId: number,
): string | undefined {
  const tradeData = tradeDataStore.getState().tradeData;
  const trade = tradeData.find((t) => t.esid === esid);

  if (!trade) {
    return undefined;
  }

  // For RP trades, legs are in the legs array
  if (trade.role === "RP" && trade.legs) {
    const leg = trade.legs.find((l) => l.blockId === blockId);
    return leg?.instrumentType;
  }

  // For OP trades, legs are in executionRequestCounterParties > legs
  if (trade.role === "OP" && trade.executionRequestCounterParties) {
    for (const cp of trade.executionRequestCounterParties) {
      if (cp.legs) {
        const leg = cp.legs.find((l) => l.blockId === blockId);
        if (leg) {
          return leg.instrumentType;
        }
      }
    }
  }

  return undefined;
}

/**
 * Gets the fills array for a specific leg/block from the Zustand store.
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID
 * @returns The fills array or empty array
 */
export function getFillsFromStore(esid: string, blockId: number): IOrderFill[] {
  const tradeData = tradeDataStore.getState().tradeData;
  const trade = tradeData.find((t) => t.esid === esid);

  if (!trade) {
    return [];
  }

  // For RP trades, fills are in the legs array
  if (trade.role === "RP" && trade.legs) {
    const leg = trade.legs.find((l) => l.blockId === blockId);
    return leg?.fills || [];
  }

  // For OP trades, fills are in executionRequestCounterParties > legs
  if (trade.role === "OP" && trade.executionRequestCounterParties) {
    for (const cp of trade.executionRequestCounterParties) {
      if (cp.legs) {
        const leg = cp.legs.find((l) => l.blockId === blockId);
        if (leg?.fills) {
          return leg.fills;
        }
      }
    }
  }

  return [];
}

/**
 * Calculates the total filled quantity (sum of all fill quantities).
 * Excludes cancelled fills from the total.
 *
 * @param fills - Array of fills
 * @returns Total quantity filled
 */
export function calculateTotalFilledQuantity(fills: IOrderFill[]): number {
  if (!fills || fills.length === 0) {
    return 0;
  }

  return fills
    .filter((fill) => !isFillCancelled(fill))
    .reduce((sum, fill) => sum + (fill.fillSize || 0), 0);
}

/**
 * Calculates the blended weighted average price.
 * Formula: Σ(fill.fillRate × fill.fillSize) / Σ(fill.fillSize)
 * Excludes cancelled fills from the calculation.
 *
 * @param fills - Array of fills
 * @returns Weighted average price or null if no valid data
 */
export function calculateBlendedPrice(fills: IOrderFill[]): number | null {
  if (!fills || fills.length === 0) {
    return null;
  }

  let totalWeightedPrice = 0;
  let totalQuantity = 0;

  for (const fill of fills) {
    // Skip cancelled fills
    if (isFillCancelled(fill)) {
      continue;
    }
    const rate = parseRate(fill.fillRate);
    if (rate !== undefined && fill.fillSize) {
      totalWeightedPrice += rate * fill.fillSize;
      totalQuantity += fill.fillSize;
    }
  }

  if (totalQuantity === 0) {
    return null;
  }

  return totalWeightedPrice / totalQuantity;
}

/**
 * Calculates the total filled percentage for the block.
 * Formula: (Σ fill.fillSize / block.notionalLeg1) × 100
 *
 * @param fills - Array of fills
 * @param blockNotional - The total notional of the block
 * @returns Percentage filled (0-100)
 */
export function calculateTotalFilledPercent(
  fills: IOrderFill[],
  blockNotional: number,
): number {
  if (!fills || fills.length === 0 || !blockNotional || blockNotional === 0) {
    return 0;
  }

  const totalFilled = calculateTotalFilledQuantity(fills);
  return (totalFilled / blockNotional) * 100;
}

/**
 * Gets fills and calculates summary data from AG Grid params.
 *
 * @param params - AG Grid cell params
 * @returns Object with fills and calculated values
 */
export function getFillsSummaryData(params: any) {
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid = fullTradeData?.esid;
  const blockId = params.data?.blockId;
  const blockNotional = params.data?.notionalLeg1;

  if (!esid || blockId === undefined) {
    return {
      fills: [],
      totalQuantity: 0,
      blendedPrice: null,
      filledPercent: 0,
    };
  }

  const fills = getFillsFromStore(esid, blockId);

  return {
    fills,
    totalQuantity: calculateTotalFilledQuantity(fills),
    blendedPrice: calculateBlendedPrice(fills),
    filledPercent: calculateTotalFilledPercent(fills, blockNotional),
  };
}

/**
 * Leg summary data structure with pre-calculated fill values from the API.
 * fillPrice is string for $fr notation (e.g., "99-16"), number for all others.
 */
export interface LegSummaryData {
  fillQuantity: number | null;
  fillPrice: number | string | null;
  percentFilled: number | null;
  instrumentType: string | undefined;
  notation: NotationToDecimalValueType | undefined;
}

/**
 * Gets the pre-calculated fill summary data from the legs array in the store.
 * This data comes directly from the API and doesn't need to be calculated from individual fills.
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID
 * @param paramsData - Optional params.data from AG Grid (fallback for direct leg data)
 * @returns LegSummaryData with fillQuantity, fillPrice, percentFilled, and instrumentType
 */
export function getLegSummaryDataFromStore(
  esid: string,
  blockId: number,
  paramsData?: any,
): LegSummaryData {
  const tradeData = tradeDataStore.getState().tradeData;
  const trade = tradeData.find((t) => t.esid === esid);

  const defaultResult: LegSummaryData = {
    fillQuantity: null,
    fillPrice: null,
    percentFilled: null,
    instrumentType: undefined,
    notation: undefined,
  };

  // Helper: for $fr notation, return the raw string price; otherwise parse to number
  function resolveFillPrice(
    rawPrice: string | undefined,
    legNotation?: string,
  ): number | string | null {
    if (rawPrice === undefined || rawPrice === null || rawPrice === "")
      return null;
    // $fr: if the price is a fraction string, return as-is
    if (legNotation === "$fr" && containsHyphenInQuote(rawPrice)) {
      return rawPrice;
    }
    return parseRate(rawPrice) ?? null;
  }

  if (!trade) {
    // Fallback: try to get data directly from params.data (the leg row)
    if (paramsData) {
      const legNotation = paramsData.notation as string | undefined;
      return {
        fillQuantity: paramsData.fillQuantity ?? null,
        fillPrice: resolveFillPrice(paramsData.fillPrice, legNotation),
        percentFilled: paramsData.percentFilled ?? null,
        instrumentType: paramsData.instrumentType,
        notation: legNotation as NotationToDecimalValueType | undefined,
      };
    }
    return defaultResult;
  }

  // For RP trades, legs are in the legs array
  if (trade.role === "RP" && trade.legs) {
    const leg = trade.legs.find((l: any) => l.blockId === blockId);
    if (leg) {
      const legNotation = leg.notation as string | undefined;
      return {
        fillQuantity: leg.fillSize ?? null,
        fillPrice: resolveFillPrice(leg.fillRate, legNotation),
        percentFilled: leg.percentFilled ?? null,
        instrumentType: leg.instrumentType,
        notation: legNotation as NotationToDecimalValueType | undefined,
      };
    }
  }

  // For OP trades, legs are in executionRequestCounterParties > legs
  if (trade.role === "OP" && trade.executionRequestCounterParties) {
    for (const cp of trade.executionRequestCounterParties) {
      if (cp.legs) {
        const leg = cp.legs.find((l: any) => l.blockId === blockId);
        if (leg) {
          const legNotation = leg.notation as string | undefined;
          return {
            fillQuantity: leg.fillQuantity ?? null,
            fillPrice: resolveFillPrice(leg.fillPrice, legNotation),
            percentFilled: leg.percentFilled ?? null,
            instrumentType: leg.instrumentType,
            notation: legNotation as NotationToDecimalValueType | undefined,
          };
        }
      }
    }
  }

  // Fallback: try to get data directly from params.data (the leg row)
  if (paramsData) {
    const legNotation = paramsData.notation as string | undefined;
    return {
      fillQuantity: paramsData.fillQuantity ?? null,
      fillPrice: resolveFillPrice(paramsData.fillPrice, legNotation),
      percentFilled: paramsData.percentFilled ?? null,
      instrumentType: paramsData.instrumentType,
      notation: legNotation as NotationToDecimalValueType | undefined,
    };
  }

  return defaultResult;
}
